import { create } from 'zustand';
import { nodePathResolver } from '../utils/nodePathResolver';
import { NodeStatus, ProjectManifest } from '../types/artifactory';
import { artifactoryService } from '../services/artifactoryService';

interface HydrationState {
  // Core state
  manifestId: string | null;
  sessionId: string | null;
  focusNodeId: string | null;
  hydrationPlan: any | null;
  manifest: ProjectManifest | null;
  
  // Tracking state
  isLoading: boolean;
  error: string | null;
  selectedNodes: string[];
  currentlyHydrating: string | null;
  isAutoHydrating: boolean;
  completedNodes: Set<string>;
  failedNodes: Set<string>;
  nodeStatuses: Record<string, NodeStatus>;
  nodeErrors: Record<string, string>;
  
  // Actions
  initialize: (manifestId: string, sessionId: string, focusNodeId?: string) => Promise<void>;
  buildHydrationPlan: (manifest: ProjectManifest | null) => void;
  selectNode: (nodeId: string) => void;
  selectAllNodes: () => void;
  clearSelection: () => void;
  hydrateNode: (nodeIdOrPath: string) => Promise<void>;
  startAutoHydration: () => Promise<void>;
  stopAutoHydration: () => void;
  reset: () => void;
}

export const useHydrationStore = create<HydrationState>((set, get) => ({
  // Initial state
  manifestId: null,
  sessionId: null,
  focusNodeId: null,
  hydrationPlan: null,
  manifest: null,
  
  isLoading: false,
  error: null,
  selectedNodes: [],
  currentlyHydrating: null,
  isAutoHydrating: false,
  completedNodes: new Set<string>(),
  failedNodes: new Set<string>(),
  nodeStatuses: {},
  nodeErrors: {},
  
  // Initialize the store with required IDs
  initialize: async (manifestId, sessionId, focusNodeId) => {
    set({ 
      manifestId, 
      sessionId, 
      focusNodeId,
      isLoading: true,
      error: null
    });
    
    try {
      // Fetch hydration plan from API
      const hydrationPlan = await artifactoryService.getHydrationPlan(manifestId, focusNodeId);
      // console.log("Raw hydration plan from API:", hydrationPlan);
      
      // Get the manifest to merge dependency information
      const manifest = await artifactoryService.getManifest(manifestId);
      // console.log("Manifest nodes:", manifest ? Object.keys(manifest.nodes).length : 0);
      
      // Enhance the hydration plan with dependency information from the manifest
      let enhancedOrderedPlan = hydrationPlan.ordered_plan;
      if (hydrationPlan.ordered_plan && manifest) {
        enhancedOrderedPlan = hydrationPlan.ordered_plan.map(node => {
          const manifestNode = manifest.nodes[node.id];
          if (manifestNode && manifestNode.dependencies && manifestNode.dependencies.length > 0) {
            // Format dependencies for display
            const formattedDeps = manifestNode.dependencies.map(depId => {
              // Try to resolve the dependency ID
              const resolvedDepId = nodePathResolver.resolveToNodeId(depId, manifest);
              if (!resolvedDepId) {
                return {
                  id: depId,
                  path: depId.includes('/') ? depId : "Unknown",
                  status: "ghost"
                };
              }
              
              const depNode = manifest.nodes[resolvedDepId];
              return {
                id: resolvedDepId,
                path: depNode.path || "Unknown",
                status: depNode.status || "ghost"
              };
            });
            
            const enhancedNode = {
              ...node,
              dependencies: formattedDeps
            };
            // console.log(`Enhanced node ${node.id} with dependencies:`, formattedDeps);
            return enhancedNode;
          }
          return node;
        });
      }
      
      // Create a new hydration plan object instead of mutating the existing one
      const enhancedHydrationPlan = {
        ...hydrationPlan,
        ordered_plan: enhancedOrderedPlan
      };
      
      // Initialize node statuses
      const nodeStatusMap: Record<string, NodeStatus> = {};
      if (hydrationPlan.ordered_plan) {
        hydrationPlan.ordered_plan.forEach((node: any) => {
          nodeStatusMap[node.id] = node.status as NodeStatus;
        });
      }
      
      // Collect all node IDs that should be selected by default
      const allNodeIds = new Set<string>();
      
      // Add all nodes from the ordered plan
      if (hydrationPlan.ordered_plan) {
        hydrationPlan.ordered_plan.forEach((node: any) => {
          allNodeIds.add(node.id);
          
          // If this is the focus node, also add all its dependencies
          if (node.id === focusNodeId && Array.isArray(node.dependencies)) {
            node.dependencies.forEach((dep: any) => {
              allNodeIds.add(dep.id);
            });
          }
        });
      }
      
      // console.log("Enhanced hydration plan:", enhancedHydrationPlan);
      
      set({
        hydrationPlan: enhancedHydrationPlan,
        manifest,
        nodeStatuses: nodeStatusMap,
        selectedNodes: Array.from(allNodeIds),
        isLoading: false
      });
    } catch (error: any) {
      console.error("Error fetching hydration plan:", error);
      set({ 
        error: "Failed to load hydration plan: " + (error.message || "Unknown error"),
        isLoading: false
      });
    }
  },
  
  // Build hydration plan locally from manifest
  buildHydrationPlan: (manifest) => {
    if (!manifest) {
      set({ hydrationPlan: null });
      return;
    }
    
    const { focusNodeId } = get();
    set({ isLoading: true, error: null, manifest });
    
    try {
      // Add logging
      // console.log("Building hydration plan for manifest:", manifest.id);
      // console.log("Focus node ID:", focusNodeId);
      
      // Build dependency graph
      const graph = new Map<string, Set<string>>();
      const nodesToProcess = new Set<string>();
      const processedNodes = new Set<string>();
      const orderedPlan: any[] = [];
      
      // If we have a focus node, start with it
      if (focusNodeId && manifest.nodes[focusNodeId]) {
        nodesToProcess.add(focusNodeId);
        // Add logging
        console.log(`Starting with focus node: ${focusNodeId}, path: ${manifest.nodes[focusNodeId].path}`);
        console.log(`Focus node dependencies:`, manifest.nodes[focusNodeId].dependencies);
      } else {
        // Otherwise, find all nodes that need hydration
        for (const [nodeId, node] of Object.entries(manifest.nodes)) {
          if (node.status === "ghost" || node.status === "stale") {
            nodesToProcess.add(nodeId);
          }
        }
        console.log(`Found ${nodesToProcess.size} nodes that need hydration`);
      }
      
      // Helper function to recursively add a node and its dependencies
      const addNodeAndDependencies = (nodeId: string) => {
        if (processedNodes.has(nodeId)) return;
        
        const node = manifest.nodes[nodeId];
        if (!node) return;
        
        // First process all dependencies
        if (node.dependencies && Array.isArray(node.dependencies)) {
          for (const depId of node.dependencies) {
            // Try to resolve the dependency ID
            const resolvedDepId = nodePathResolver.resolveToNodeId(depId, manifest);
            if (resolvedDepId && !processedNodes.has(resolvedDepId)) {
              nodesToProcess.add(resolvedDepId);
              addNodeAndDependencies(resolvedDepId);
            }
          }
        }
        
        // Now add this node
        if (!processedNodes.has(nodeId)) {
          processedNodes.add(nodeId);
          
          // Add more detailed logging for raw dependencies
          // console.log(`Node ${nodeId} (${node.path}) has raw dependencies:`, node.dependencies);
          
          // Format dependencies for display
          const formattedDeps = (Array.isArray(node.dependencies) ? node.dependencies : []).map(depId => {
            // Try to resolve the dependency ID
            const resolvedDepId = nodePathResolver.resolveToNodeId(depId, manifest);
            if (!resolvedDepId) {
              // console.log(`Could not resolve dependency ID: ${depId} for node ${nodeId}`);
              return {
                id: depId,
                path: depId.includes('/') ? depId : "Unknown",
                status: "ghost"
              };
            }
            
            const depNode = manifest.nodes[resolvedDepId];
            // console.log(`Resolved dependency ${depId} to ${resolvedDepId}, path: ${depNode.path}`);
            return {
              id: resolvedDepId,
              path: depNode.path || "Unknown",
              status: depNode.status || "ghost"
            };
          });
          
          // console.log(`Node ${nodeId} formatted dependencies:`, formattedDeps);
          // console.log(`Formatted dependencies length: ${formattedDeps.length}`);
          
          // Add to ordered plan
          orderedPlan.push({
            id: nodeId,
            path: node.path,
            status: node.status,
            dependencies: formattedDeps
          });
        }
      };
      
      // Process all nodes that need to be included
      for (const nodeId of nodesToProcess) {
        addNodeAndDependencies(nodeId);
      }
      
      // Ensure topological ordering - dependencies first
      const sortedPlan: any[] = [];
      const visited = new Set<string>();
      const tempVisited = new Set<string>(); // For cycle detection
      
      // Helper function for topological sort - ensures dependencies come first
      const addToSortedPlan = (nodeId: string) => {
        // Skip if already processed
        if (visited.has(nodeId)) return;
        
        // Check for cycles
        if (tempVisited.has(nodeId)) {
          console.warn(`Cycle detected involving node ${nodeId}, skipping`);
          return;
        }
        
        tempVisited.add(nodeId);
        
        const node = manifest.nodes[nodeId];
        if (!node) {
          tempVisited.delete(nodeId);
          return;
        }
        
        // First process all dependencies
        for (const depId of node.dependencies) {
          const resolvedDepId = nodePathResolver.resolveToNodeId(depId, manifest);
          if (resolvedDepId && manifest.nodes[resolvedDepId]) {
            addToSortedPlan(resolvedDepId);
          }
        }
        
        // Remove from temp visited
        tempVisited.delete(nodeId);
        
        // Mark as visited
        visited.add(nodeId);
        
        // Find or create plan node
        const planNode = orderedPlan.find(n => n.id === nodeId);
        if (planNode) {
          sortedPlan.push(planNode);
        } else {
          // Create a new plan node if needed
          const formattedDeps = node.dependencies.map(depId => {
            const resolvedDepId = nodePathResolver.resolveToNodeId(depId, manifest);
            if (!resolvedDepId) {
              return {
                id: depId,
                path: depId.includes('/') ? depId : "Unknown",
                status: "ghost"
              };
            }
            
            const depNode = manifest.nodes[resolvedDepId];
            return {
              id: resolvedDepId,
              path: depNode.path || "Unknown",
              status: depNode.status || "ghost"
            };
          });
          
          sortedPlan.push({
            id: nodeId,
            path: node.path,
            status: node.status,
            dependencies: formattedDeps
          });
        }
      };
      
      // Process all nodes for topological ordering
      for (const nodeId of processedNodes) {
        addToSortedPlan(nodeId);
      }
      
      // Validate the sorted plan to ensure dependencies come before dependents
      const nodeIndices = new Map<string, number>();
      sortedPlan.forEach((node, index) => {
        nodeIndices.set(node.id, index);
      });
      
      let hasOrderIssues = false;
      sortedPlan.forEach((node, index) => {
        if (node.dependencies && Array.isArray(node.dependencies)) {
          node.dependencies.forEach(dep => {
            const depIndex = nodeIndices.get(dep.id);
            if (depIndex !== undefined && depIndex > index) {
              console.warn(`Dependency order issue: ${dep.id} should come before ${node.id}`);
              hasOrderIssues = true;
            }
          });
        }
      });
      
      // If order issues were detected, perform a second pass sort
      if (hasOrderIssues) {
        console.warn("Dependency order issues detected, performing second pass sort");
        
        // Clear previous data
        visited.clear();
        const secondPassPlan: any[] = [];
        
        // Second pass with stricter ordering
        const addToSecondPassPlan = (nodeId: string) => {
          if (visited.has(nodeId)) return;
          
          const node = sortedPlan.find(n => n.id === nodeId);
          if (!node) return;
          
          // Process all dependencies first
          if (node.dependencies && Array.isArray(node.dependencies)) {
            for (const dep of node.dependencies) {
              addToSecondPassPlan(dep.id);
            }
          }
          
          // Add this node only after all dependencies
          if (!visited.has(nodeId)) {
            visited.add(nodeId);
            secondPassPlan.push(node);
          }
        };
        
        // Process all nodes again
        for (const node of sortedPlan) {
          addToSecondPassPlan(node.id);
        }
        
        // Use the second pass plan if it has nodes
        if (secondPassPlan.length > 0) {
          console.log("Using second pass sorted plan");
          sortedPlan.length = 0; // Clear the array
          sortedPlan.push(...secondPassPlan);
        }
      }
      
      // Count nodes that need hydration
      const nodesToHydrate = sortedPlan.filter(
        node => node.status === "ghost" || node.status === "stale"
      ).length;
      
      // Reverse for UI display (dependencies first)
      sortedPlan.reverse();
      
      // Log the sorted plan
      // console.log("Sorted hydration plan:", sortedPlan.map(node => ({
      //   id: node.id,
      //   path: node.path,
      //   dependencies: node.dependencies?.length || 0
      // })));
      
      // Initialize node statuses
      const nodeStatusMap: Record<string, NodeStatus> = {};
      sortedPlan.forEach((node: any) => {
        nodeStatusMap[node.id] = node.status as NodeStatus;
      });
      
      // Collect all node IDs that should be selected by default
      const allNodeIds = new Set<string>();
      
      // Add all nodes from the ordered plan
      sortedPlan.forEach((node: any) => {
        allNodeIds.add(node.id);
        
        // If this is the focus node, also add all its dependencies
        if (node.id === focusNodeId && Array.isArray(node.dependencies)) {
          node.dependencies.forEach((dep: any) => {
            allNodeIds.add(dep.id);
          });
        }
      });
      
      set({
        hydrationPlan: {
          nodes_to_hydrate: nodesToHydrate,
          ordered_plan: sortedPlan,
          focus_node_id: focusNodeId || null
        },
        nodeStatuses: nodeStatusMap,
        selectedNodes: Array.from(allNodeIds),
        isLoading: false
      });
    } catch (error: any) {
      console.error("Error building local hydration plan:", error);
      set({ 
        error: "Failed to build hydration plan: " + (error.message || "Unknown error"),
        isLoading: false
      });
    }
  },
  
  // Select/deselect a node
  selectNode: (nodeId) => {
    const { selectedNodes } = get();
    if (selectedNodes.includes(nodeId)) {
      set({ selectedNodes: selectedNodes.filter(id => id !== nodeId) });
    } else {
      set({ selectedNodes: [...selectedNodes, nodeId] });
    }
  },
  
  // Select all nodes
  selectAllNodes: () => {
    const { hydrationPlan, focusNodeId } = get();
    if (!hydrationPlan) return;
    
    const allNodeIds = new Set<string>();
    
    // Add all nodes from the ordered plan
    hydrationPlan.ordered_plan.forEach((node: any) => {
      allNodeIds.add(node.id);
      
      // Add dependencies if this is the focus node
      if (node.id === focusNodeId && Array.isArray(node.dependencies)) {
        node.dependencies.forEach((dep: any) => {
          allNodeIds.add(dep.id);
        });
      }
    });
    
    set({ selectedNodes: Array.from(allNodeIds) });
  },
  
  // Clear selection
  clearSelection: () => {
    set({ selectedNodes: [] });
  },
  
  // Hydrate a single node
  hydrateNode: async (nodeIdOrPath) => {
    const { manifestId, sessionId, manifest } = get();
    
    if (!nodeIdOrPath || !manifestId || !sessionId) {
      console.error("Missing required parameters for hydrateNode");
      return;
    }
    
    // Always try to resolve to a node ID first
    const isPath = nodeIdOrPath.includes('/');
    let resolvedId = isPath 
      ? nodePathResolver.resolveToNodeId(nodeIdOrPath, manifest)
      : nodeIdOrPath;
    let displayId = nodeIdOrPath; // For UI display
    
    // Update UI state to show hydration in progress
    set(state => ({ 
      currentlyHydrating: displayId,
      nodeStatuses: { 
        ...state.nodeStatuses, 
        [displayId]: NodeStatus.HYDRATING 
      },
      nodeErrors: Object.fromEntries(
        Object.entries(state.nodeErrors).filter(([key]) => key !== displayId)
      )
    }));
    
    try {
      // Call the hydration service
      const result = await artifactoryService.hydrateNodeWithStatus(
        manifestId,
        resolvedId || nodeIdOrPath,
        sessionId,
        false // Don't hydrate dependencies automatically
      );
      
      if (result.success) {
        set(state => ({
          completedNodes: new Set([...state.completedNodes, displayId]),
          nodeStatuses: { 
            ...state.nodeStatuses, 
            [displayId]: NodeStatus.FRESH 
          }
        }));
        
        // If we're using a path and it was resolved to a node ID, update both
        if (isPath && resolvedId && resolvedId !== nodeIdOrPath) {
          set(state => ({
            completedNodes: new Set([...state.completedNodes, resolvedId!]),
            nodeStatuses: { 
              ...state.nodeStatuses, 
              [resolvedId!]: NodeStatus.FRESH 
            }
          }));
        }
      } else {
        set(state => ({
          failedNodes: new Set([...state.failedNodes, displayId]),
          nodeStatuses: { 
            ...state.nodeStatuses, 
            [displayId]: NodeStatus.STALE 
          },
          nodeErrors: {
            ...state.nodeErrors,
            [displayId]: result.error || "Unknown error occurred"
          }
        }));
      }
    } catch (error: any) {
      console.error(`Failed to hydrate ${isPath ? 'path' : 'node'} ${displayId}:`, error);
      set(state => ({
        failedNodes: new Set([...state.failedNodes, displayId]),
        nodeStatuses: { 
          ...state.nodeStatuses, 
          [displayId]: NodeStatus.STALE 
        },
        nodeErrors: {
          ...state.nodeErrors,
          [displayId]: error.message || "Unknown error occurred"
        }
      }));
    } finally {
      set({ currentlyHydrating: null });
    }
  },
  
  // Start auto-hydration process
  startAutoHydration: async () => {
    const { 
      hydrationPlan, 
      selectedNodes, 
      isAutoHydrating,
      completedNodes,
      hydrateNode
    } = get();
    
    if (!hydrationPlan || !hydrationPlan.ordered_plan || selectedNodes.length === 0 || isAutoHydrating) {
      return;
    }
    
    set({ isAutoHydrating: true });
    
    try {
      // Create a dependency map for quick lookup
      const dependencyMap = new Map<string, string[]>();
      
      // Build dependency map from the hydration plan
      for (const node of hydrationPlan.ordered_plan) {
        if (node.dependencies && Array.isArray(node.dependencies)) {
          dependencyMap.set(node.id, node.dependencies.map(dep => dep.id));
        } else {
          dependencyMap.set(node.id, []);
        }
      }
      
      // Create a list of nodes to hydrate in dependency order
      const nodesToHydrate = [...hydrationPlan.ordered_plan]
        .filter(node => selectedNodes.includes(node.id) && !get().completedNodes.has(node.id));
      
      // Disable this log
      // console.log("Starting auto-hydration with nodes:", nodesToHydrate.map(n => n.path));
      
      // Process each node in order
      for (const node of nodesToHydrate) {
        // Skip if already completed or auto-hydration was cancelled
        if (get().completedNodes.has(node.id) || !get().isAutoHydrating) {
          continue;
        }
        
        // Disable this log
        // console.log(`Processing node ${node.id} (${node.path})`);
        
        // Get dependencies for this node
        const dependencies = dependencyMap.get(node.id) || [];
        // Disable this log
        // console.log(`Node ${node.id} has dependencies:`, dependencies);
        
        // Check if all dependencies are hydrated
        const pendingDependencies = dependencies.filter(depId => 
          !get().completedNodes.has(depId) && 
          !(get().manifest?.nodes[depId]?.status === 'fresh')
        );
        
        // Disable this log
        // console.log(`Node ${node.id} has pending dependencies:`, pendingDependencies);
        
        if (pendingDependencies.length > 0) {
          // Disable this log
          // console.log(`Hydrating dependencies for ${node.id} first`);
          
          // Hydrate each dependency one by one
          for (const depId of pendingDependencies) {
            if (!get().isAutoHydrating) {
              break;
            }
            
            // Skip if already completed
            if (get().completedNodes.has(depId)) {
              continue;
            }
            
            // Disable this log
            // console.log(`Hydrating dependency ${depId}`);
            
            // Check if dependency is valid
            if (nodePathResolver.isValid(depId, get().manifest)) {
              await get().hydrateNode(depId);
            } else {
              console.warn(`Invalid dependency ${depId}, marking as failed`);
              set(state => ({
                failedNodes: new Set([...state.failedNodes, depId])
              }));
            }
          }
          
          // Re-check if all dependencies are now hydrated
          const stillPendingDeps = dependencies.filter(depId => 
            !get().completedNodes.has(depId) && 
            !(get().manifest?.nodes[depId]?.status === 'fresh')
          );
          
          if (stillPendingDeps.length > 0) {
            console.warn(`Some dependencies for ${node.id} could not be hydrated:`, stillPendingDeps);
            // Mark as failed and continue to next node
            set(state => ({
              failedNodes: new Set([...state.failedNodes, node.id])
            }));
            continue;
          }
        }
        
        // Now hydrate the node itself
        // Disable this log
        // console.log(`Hydrating node ${node.id} (${node.path})`);
        if (nodePathResolver.isValid(node.id, get().manifest)) {
          await get().hydrateNode(node.id);
        } else {
          console.warn(`Invalid node ${node.id}, marking as failed`);
          set(state => ({
            failedNodes: new Set([...state.failedNodes, node.id])
          }));
        }
      }
      
      console.log("Auto-hydration complete");
    } catch (error) {
      console.error("Error during auto-hydration:", error);
    } finally {
      set({ isAutoHydrating: false });
    }
  },
  
  // Stop auto-hydration
  stopAutoHydration: () => {
    set({ isAutoHydrating: false });
  },
  
  // Reset the store
  reset: () => {
    set({
      manifestId: null,
      sessionId: null,
      focusNodeId: null,
      hydrationPlan: null,
      manifest: null,
      isLoading: false,
      error: null,
      selectedNodes: [],
      currentlyHydrating: null,
      isAutoHydrating: false,
      completedNodes: new Set<string>(),
      failedNodes: new Set<string>(),
      nodeStatuses: {},
      nodeErrors: {}
    });
  }
}));
